import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { de } from "date-fns/locale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AbsenceManagement() {
  const navigate = useNavigate();
  const [absences, setAbsences] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    employee_id: "",
    absence_type: "vacation",
    start_date: "",
    end_date: "",
    reason: ""
  });

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (!userData || JSON.parse(userData).type !== "admin") {
      navigate("/admin");
      return;
    }
    loadAbsences();
    loadEmployees();
  }, [navigate]);

  const loadAbsences = async () => {
    try {
      const response = await axios.get(`${API}/absences`);
      setAbsences(response.data);
    } catch (error) {
      console.error("Error loading absences:", error);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data.filter(emp => emp.active));
    } catch (error) {
      console.error("Error loading employees:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/absences`, formData);
      toast.success("Abwesenheit erstellt");
      setDialogOpen(false);
      setFormData({
        employee_id: "",
        absence_type: "vacation",
        start_date: "",
        end_date: "",
        reason: ""
      });
      loadAbsences();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Möchten Sie diese Abwesenheit wirklich löschen?")) return;
    
    try {
      await axios.delete(`${API}/absences/${id}`);
      toast.success("Abwesenheit gelöscht");
      loadAbsences();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const getAbsenceTypeLabel = (type) => {
    const labels = {
      vacation: "Ferien",
      sick: "Krankheit",
      other: "Andere"
    };
    return labels[type] || type;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/admin/dashboard")}
              className="rounded-full"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-4xl font-bold text-gray-800" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Abwesenheiten
            </h1>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setFormData({
                employee_id: "",
                absence_type: "vacation",
                start_date: "",
                end_date: "",
                reason: ""
              });
            }
          }}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="add-absence-button">
                <Plus className="w-4 h-4 mr-2" />
                Neue Abwesenheit
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md rounded-3xl">
              <DialogHeader>
                <DialogTitle>Neue Abwesenheit</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="employee">Mitarbeiter</Label>
                  <Select
                    value={formData.employee_id}
                    onValueChange={(value) => setFormData({ ...formData, employee_id: value })}
                    required
                  >
                    <SelectTrigger className="rounded-2xl" data-testid="employee-select">
                      <SelectValue placeholder="Mitarbeiter wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="type">Art</Label>
                  <Select
                    value={formData.absence_type}
                    onValueChange={(value) => setFormData({ ...formData, absence_type: value })}
                  >
                    <SelectTrigger className="rounded-2xl" data-testid="absence-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vacation">Ferien</SelectItem>
                      <SelectItem value="sick">Krankheit</SelectItem>
                      <SelectItem value="other">Andere</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="start_date">Von</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      required
                      className="rounded-2xl"
                      data-testid="start-date-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="end_date">Bis</Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      required
                      className="rounded-2xl"
                      data-testid="end-date-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="reason">Grund (optional)</Label>
                  <Input
                    id="reason"
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    className="rounded-2xl"
                    data-testid="reason-input"
                  />
                </div>
                <Button type="submit" className="w-full rounded-full" data-testid="save-absence-button">
                  Erstellen
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid gap-4">
          {absences.length === 0 ? (
            <Card className="shadow-xl rounded-3xl border-0">
              <CardContent className="p-12 text-center">
                <p className="text-gray-500">Keine Abwesenheiten erfasst</p>
              </CardContent>
            </Card>
          ) : (
            absences.map((absence) => (
              <Card key={absence.id} className="shadow-lg rounded-3xl border-0" data-testid={`absence-card-${absence.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-1">{absence.employee_name}</h3>
                      <div className="flex items-center gap-3 mb-1">
                        <span className="px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800">
                          {getAbsenceTypeLabel(absence.absence_type)}
                        </span>
                        <span className="text-gray-600">
                          {format(new Date(absence.start_date), "dd.MM.yyyy", { locale: de })} - 
                          {format(new Date(absence.end_date), "dd.MM.yyyy", { locale: de })}
                        </span>
                      </div>
                      {absence.reason && (
                        <p className="text-gray-600 text-sm">{absence.reason}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(absence.id)}
                        className="rounded-full"
                        data-testid={`delete-${absence.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
